import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  DATE_ONLY_INPUT,
  INTEGER_INPUT,
  OPTIONAL_DATE_ONLY_INPUT,
} from "../../types/socketInputs";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Transfer Order Line
 *
 * Takes ONE transfer-order LINE as single-value input sockets, with its
 * header's fields alongside, shaped after the Gently model
 * (`im_transfer_orders` + `im_transfer_order_line_items`) — not after any
 * source system. The service groups lines into transfers and writes both
 * halves; a CDC observer or file parser maps its own columns into these
 * sockets.
 *
 * The internal-movement half of the SUPTRANS ingest. AS400 models a
 * warehouse-to-warehouse transfer as a purchase order whose "vendor" is another
 * branch, so the same source rows feed this step and
 * `upsert_purchase_order_line`: a vendor code that resolves to a WAREHOUSE is a
 * transfer and lands here, one that resolves to a SUPPLIER is a purchase and
 * lands there. Each step reports the rows it could not resolve, so nothing is
 * silently dropped by the split.
 *
 * A leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertTransferOrderLineStep = defineStepConfig({
  type: "upsert_transfer_order_line",
  name: "Upsert Transfer Order Line",
  description:
    "Upserts one transfer-order line and its header (order/line origin ids, source and destination warehouse codes, product SKU, dates, status, quantity). Resolves the warehouses and product, then writes both tables in source-agnostic batches. Fan a trigger's per-field array outputs into its single-value inputs and add a batch config to process rows in chunks.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    // Nullable for the same reason as the other upsert steps: input is
    // validated BEFORE the handler runs, a parse failure is non-retryable, and
    // the batch path shares one activity per chunk — so a non-nullable socket
    // would fail a whole chunk of good rows over a few junk ones.
    orderOriginId: z
      .string()
      .nullable()
      .describe(
        "The TRANSFER's natural key in the source system; null is unresolvable",
      ),
    lineOriginId: z
      .string()
      .nullable()
      .describe(
        "The LINE's natural key, unique per line — not per transfer. Must distinguish lines a transfer repeats (AS400 reuses a line number across ship-from locations). Null is unresolvable.",
      ),
    sourceWarehouseCode: z
      .string()
      .nullable()
      .describe(
        "Origin code of the warehouse the goods LEAVE. Required: a row whose source does not resolve to a warehouse is not a transfer at all — it belongs on `upsert_purchase_order_line`.",
      ),
    destinationWarehouseCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Origin code of the RECEIVING warehouse. Lands on the header (last row wins) AND on this line as its own destination — a transfer's lines can go to different branches, and open supply is counted per warehouse. An unresolved code is reported and the transfer still lands without one.",
      ),
    productSku: z
      .string()
      .nullable()
      .describe(
        "Product SKU. An unknown or missing SKU does NOT drop the line — it lands with no product reference and is reported.",
      ),
    transferNumber: z
      .string()
      .nullable()
      .optional()
      .describe(
        "The transfer's human-facing number. Defaults to the order origin id.",
      ),
    orderDate: DATE_ONLY_INPUT.nullable().describe(
      "Order date, YYYY-MM-DD. Required — null is unresolvable.",
    ),
    shipDate: OPTIONAL_DATE_ONLY_INPUT.describe("Ship date, YYYY-MM-DD"),
    deliveryDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "Expected delivery date, YYYY-MM-DD. Lands on the header (last row wins) AND on this line as its own expected date — a transfer's lines can carry different ETAs.",
    ),
    // Per LINE, not per transfer — see the purchase-order sibling. Lead time is
    // measured line by line, off `receivedDate - orderedDate`.
    orderedDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "When THIS LINE was ordered, YYYY-MM-DD. Source-reported, per line — not the header's order date.",
    ),
    receivedDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "When THIS LINE was received, YYYY-MM-DD. Source-reported; the other half of observed lead time.",
    ),
    lastSeenAt: z
      .union([z.string(), z.date()])
      .nullable()
      .optional()
      .describe(
        "When the SOURCE last asserted this transfer — wire it to the source row's own change timestamp, not to `now`. A full-state export makes a removal an absence rather than an event, so this is what lets a later reconcile tell a transfer that is still open from one the source stopped sending.",
      ),
    // Free-form on this table by design: ingested transfers carry AS400's
    // vocabulary alongside the values replenishment writes. Nothing validates
    // it, so map source codes upstream.
    status: z
      .string()
      .nullable()
      .describe(
        "Transfer status, e.g. in_transit, received, closed, cancelled. Free-form — this table holds both ingested and replenishment-created vocabularies, so map source codes upstream.",
      ),
    currencyCode: z
      .string()
      .nullable()
      .optional()
      .describe("ISO currency code"),
    quantity: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Total transferred quantity for the header. Leave unwired and it is recomputed from the transfer's persisted lines after each write — correct however the batch chunked.",
      ),
    notes: z
      .array(z.string())
      .nullable()
      .optional()
      .describe("Human-readable provenance notes stored on the header"),
    // INTEGER_INPUT, not a bare z.number().int(): ingest foreign-table columns
    // are TEXT, so a socket wired straight to one delivers "3".
    lineNumber: INTEGER_INPUT.nullable()
      .optional()
      .describe(
        "Line number as shown on the transfer. Display only — not a key.",
      ),
    lineQuantity: QUANTITY_INPUT.nullable().describe(
      "Quantity on this line. Required — null is unresolvable.",
    ),
    shippedQuantity: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Quantity already shipped. Open supply is `lineQuantity - shippedQuantity`, so leaving this unwired counts the whole line as still inbound.",
      ),
    description: z
      .string()
      .nullable()
      .optional()
      .describe("Line description. Defaults to the product SKU."),
    unitOfMeasure: z.string().nullable().optional().describe("Unit of measure"),
    lineMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Stored on the line as origin_metadata"),
    orderMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Stored on the transfer header as origin_metadata"),
    // Same contract as upsert_product's changeOperation, with one difference:
    // that sink only refuses to write a deleted row back, while this one ACTS
    // on the delete by closing the still-open line. A delete's change event
    // carries the row's before-image (key columns only), so every other
    // socket is ignored for these rows.
    changeOperation: z
      .enum(["insert", "update", "delete"])
      .optional()
      .describe(
        "CDC operation for this row. Wire from the Observe trigger's Change Operation output. A 'delete' means the source removed the row: the line is closed if still open (header status re-derived, close_reason 'source_removed'), and a line the source already reported terminal keeps that report. Left unwired, every row is an ordinary upsert - and a CDC-fed workflow then writes deleted rows back, so wire it on any CDC path.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The transfer and line were upserted"),
    closedByDelete: z
      .boolean()
      .describe(
        "The row was a CDC delete and its still-open line was closed. False for a delete whose line was already terminal or was never written by this sink (a purchase-order row reaching this step matches nothing, by design).",
      ),
    skipped: z
      .boolean()
      .describe(
        "The row was not written: an unresolvable source warehouse, or a missing key/date/status/quantity. An unknown product or destination alone does not skip a row.",
      ),
    unresolvedSourceWarehouse: z
      .boolean()
      .describe(
        "No warehouse matched the source code — the row is probably a supplier purchase, not a transfer",
      ),
    unresolvedDestinationWarehouse: z
      .boolean()
      .describe("A destination code was given but matched no warehouse"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "The SKU matched NEITHER the legacy product catalog nor the v3 one. The line was still written, without a product reference — check `written`, not this, to know whether it landed.",
      ),
    invalidRow: z
      .boolean()
      .describe(
        "Missing order/line origin id, order date, status or line quantity, or a quantity that is not a number",
      ),
  }),

  configSchema: z.object({
    // Chunks run concurrently unless the definition sets
    // `fanoutStrategy: "sequential"`. Lines are keyed individually and the
    // header aggregate is recomputed from persisted rows, so both are
    // chunk-order-independent — but for a transfer whose lines SPAN chunks, the
    // header's status, dates and a supplied quantity are decided by completion order rather
    // than source order.
    //
    // `fanoutStrategy: "sequential"` is the fix. A larger size is NOT one:
    // chunking splits the input at fixed offsets, so a transfer starting near a
    // boundary straddles it however few lines it has. The only size that helps
    // is one covering the WHOLE input, which collapses the fanout to a single
    // chunk.
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    // Trimmed and non-empty: this value goes verbatim into both natural keys
    // and into warehouse resolution. A blank or padded one would key rows on
    // "" (or " as400 ") and quietly never match again on the next run.
    originSystem: z
      .string()
      .trim()
      .min(1)
      .describe(
        "Stamped on the transfer and line, and half of each natural key. Also the default filter for WAREHOUSE and PRODUCT resolution — override it below when the branch catalog was ingested under a different value.",
      ),
    // Trimmed and non-empty — see the purchase-order sibling: a blank or padded
    // override becomes an exact filter that matches nothing.
    warehouseOriginSystem: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Restrict WAREHOUSE and PRODUCT resolution to references from this origin system. Defaults to `originSystem`. The purchase-order sibling has a supplier counterpart; transfers reference no supplier.",
      ),
    originEntity: z
      .string()
      .optional()
      .describe(
        "Stamped on the transfer and its lines for provenance, e.g. the source table name. Not part of any key.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
